import { Image } from '#app/components/Image/Image.js';
import { type ImageProps } from '#app/components/Image/types.js';
import { Text } from '#app/components/Primitives/Text.js';
import { Video } from '#app/components/Video/Video.js';
import {
  MOBILE_PROJECT_COVER_WIDTH,
  PROJECT_COVER_ASPECT_RATIO,
} from './constants.js';
import { Cover } from './ProjectCover.styles.js';
import { type ProjectCoverBaseProps } from './types.js';
import { css, styled } from 'styled-components';

const Title = styled(Text).attrs({
  as: 'h6',
  textStyle: 'subtitleRegular',
})`
  ${({ theme }) => css`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    width: 100%;
    padding: 16px;
    color: ${theme.colorsV2.white};
    background: linear-gradient(
      0deg,
      rgba(0, 0, 0, 0.4) 20%,
      rgba(0, 0, 0, 0) 60%
    );
    border-bottom-left-radius: inherit;
    border-bottom-right-radius: inherit;
    text-shadow: 0px 2px 4px rgba(0, 0, 0, 0.25);

    opacity: 0;
    transition: opacity 175ms ease-out;
  `}
`;

const Container = styled.div<{ $readOnly: boolean; $textCover: boolean }>`
  ${({ $readOnly, $textCover }) => css`
    width: 100%;
    position: relative;
    border-radius: inherit;
    background: #eee;

    padding-top: ${$textCover
      ? `${100 / PROJECT_COVER_ASPECT_RATIO}%`
      : 'initial'};

    &:hover ${Title} {
      opacity: 1;
    }

    ${!$readOnly &&
    css`
      cursor: pointer;
    `}

    & > picture {
      display: flex;
    }

    & > div {
      border-radius: inherit;
      display: block;
    }
  `}
`;

const ProjectCoverContents = ({
  fillHeight,
  image,
  imageAlt,
  objectFit,
  onLoad,
  removeAnimation = false,
  textCoverStyle,
  title,
  width = MOBILE_PROJECT_COVER_WIDTH,
}: ProjectCoverBaseProps) => {
  if (!image) {
    return <Cover textCoverStyle={textCoverStyle} />;
  }

  if (image.animated && !removeAnimation) {
    return (
      <Video
        assetType="image"
        autoPlay
        controls={false}
        height={Math.floor(width / PROJECT_COVER_ASPECT_RATIO)}
        layout={fillHeight ? 'fill' : 'intrinsic'}
        loop
        muted
        objectFit={objectFit}
        onLoad={onLoad}
        playsInline
        transformOptions={{
          aspectRatio: PROJECT_COVER_ASPECT_RATIO,
          crop: 'fill',
          quality: 'auto:best',
        }}
        uid={image.uid}
        width={width}
      />
    );
  }

  const imageTransformOptions: ImageProps['imageTransforms'] =
    removeAnimation && image?.animated ? { page: 1 } : {};

  return (
    <Image
      alt={imageAlt ?? title}
      height={Math.floor(width / PROJECT_COVER_ASPECT_RATIO)}
      imageTransforms={{
        aspectRatio: PROJECT_COVER_ASPECT_RATIO,
        crop: 'fill',
        ...imageTransformOptions,
      }}
      layout={fillHeight ? 'fill' : 'intrinsic'}
      objectFit={objectFit}
      onLoad={onLoad}
      uid={image.uid}
      width={width}
    />
  );
};

export const ProjectCover = (props: ProjectCoverBaseProps) => {
  return (
    <Container
      $readOnly={!props.onClick}
      $textCover={!props.image}
      onClick={props.onClick}
      style={
        props.fillHeight
          ? {
              height: props.fillHeight,
            }
          : {}
      }
    >
      <ProjectCoverContents {...props} />
      {props.title ? <Title>{props.title}</Title> : null}
    </Container>
  );
};
